import { useGetAllAccounts } from '@/api/generated/accounts/accounts';
import { useGetAllTransactions } from '@/api/generated/transactions/transactions';
import { NetWorthTrendCardLazy } from '@/components/dashboard/NetWorthTrendCardLazy';
import { OverviewTabs, type OverviewTab } from '@/components/dashboard/OverviewTabs';
import { PageFiltersMenu } from '@/components/layout/PageFiltersMenu';
import { PillarTabbedPage } from '@/components/layout/PillarTabbedPage';
import { useOverviewPillarHero } from '@/components/layout/pillar-heroes/OverviewPillarHero';
import { PlanningOverviewWidget } from '@/components/planning/PlanningOverviewWidget';
import {
  StackedAllocationBar,
  type AllocationSegment,
} from '@/components/planning/StackedAllocationBar';
import { ErrorState } from '@/components/ui/async-state';
import { Label } from '@/components/ui/label';
import { RollingNumber } from '@/components/ui/rolling-number';
import { SectionCard } from '@/components/ui/section-card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PnLIndicator } from '@/components/wealth/PnLIndicator';
import {
  buildNetWorthHistory,
  computeNetWorthTotals,
} from '@/features/accounts/buildNetWorthHistory';
import { aggregateHoldingsByAssetClass, useOverviewHoldings } from '@/features/trading';
import { useLanguage } from '@/i18n/language-hooks';
import { FILTER_ALL } from '@/lib/filterConstants';
import { allowanceUsedTone, PNL_TONE_TEXT } from '@/lib/ui/wealth-tone';
import { cn, formatCurrency, useDefaultCurrency } from '@/lib/utils';
import { ChevronRight, TrendingDown, TrendingUp } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

function overviewTabFromRoute(tab: string | undefined): OverviewTab {
  if (tab === 'net-worth' || tab === 'financial-plan') return tab;
  return 'summary';
}

const Overview = () => {
  const { t } = useLanguage();
  const { tab } = useParams<{ tab?: string }>();
  const activeTab = overviewTabFromRoute(tab);
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultCurrency = useDefaultCurrency();
  const { data: accountsResponse, isLoading, error: accountsError } = useGetAllAccounts();
  const accounts = useMemo(() => accountsResponse?.items ?? [], [accountsResponse]);
  const { data: transactionsResponse, isLoading: txLoading } = useGetAllTransactions();
  const transactions = useMemo(() => transactionsResponse?.items ?? [], [transactionsResponse]);
  const { holdings, isLoading: holdingsLoading } = useOverviewHoldings();

  const selectedAccountId = searchParams.get('accountId') ?? FILTER_ALL;
  const selectedAccount = useMemo(
    () =>
      selectedAccountId === FILTER_ALL
        ? null
        : (accounts.find((account) => account.id === selectedAccountId) ?? null),
    [accounts, selectedAccountId]
  );

  const setSelectedAccountId = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === FILTER_ALL) next.delete('accountId');
          else next.set('accountId', value);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const clearAccountFilter = useCallback(() => {
    setSelectedAccountId(FILTER_ALL);
  }, [setSelectedAccountId]);

  const netWorthFilterMenu = useMemo(
    () => (
      <PageFiltersMenu activeCount={selectedAccount ? 1 : 0} onClear={clearAccountFilter}>
        <div className='space-y-2'>
          <Label htmlFor='overview-net-worth-account' className='text-sm font-medium'>
            {t('overview.netWorth.accountFilter')}
          </Label>
          <Select
            value={selectedAccount ? selectedAccount.id : FILTER_ALL}
            onValueChange={setSelectedAccountId}
          >
            <SelectTrigger id='overview-net-worth-account'>
              <SelectValue placeholder={t('overview.netWorth.allAccounts')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={FILTER_ALL}>{t('overview.netWorth.allAccounts')}</SelectItem>
              {accounts.map((account) => (
                <SelectItem key={account.id} value={account.id}>
                  {account.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </PageFiltersMenu>
    ),
    [accounts, clearAccountFilter, selectedAccount, setSelectedAccountId, t]
  );

  const assetBreakdown = aggregateHoldingsByAssetClass(holdings);
  const totals = computeNetWorthTotals(accounts);
  const breakdownTotal = assetBreakdown.reduce((sum, row) => sum + row.marketValue, 0);
  const portfolioPnL = assetBreakdown.reduce((sum, row) => sum + row.unrealizedPnL, 0);

  const netWorthHistory = useMemo(
    () => buildNetWorthHistory(accounts, transactions, selectedAccount),
    [accounts, selectedAccount, transactions]
  );

  const allocationSegments: AllocationSegment[] = useMemo(
    () =>
      assetBreakdown.map((row) => ({
        id: row.assetClass,
        label: t(`trade.assetClass.${row.assetClass}`),
        percent: breakdownTotal > 0 ? (row.marketValue / breakdownTotal) * 100 : 0,
        valueLabel: holdingsLoading ? '—' : formatCurrency(row.marketValue, defaultCurrency),
      })),
    [assetBreakdown, breakdownTotal, defaultCurrency, holdingsLoading, t]
  );

  const topPerformers = useMemo(
    () =>
      [...holdings]
        .filter((h) => (h.unrealizedPnL ?? 0) !== 0)
        .sort((a, b) => (b.unrealizedPnL ?? 0) - (a.unrealizedPnL ?? 0))
        .slice(0, 5)
        .map((h) => ({
          ...h,
          pnlPct: h.costBasis > 0 ? ((h.unrealizedPnL ?? 0) / h.costBasis) * 100 : 0,
        })),
    [holdings]
  );

  const taxWrappers = useMemo(
    () =>
      accounts
        .filter((a) => a.capabilities?.includes('investing') && a.productWrapper)
        .map((account) => {
          const wrapper = account.productWrapper!;
          const ratio =
            wrapper.allowanceLimit > 0 ? wrapper.allowanceUsed / wrapper.allowanceLimit : 0;
          return {
            id: account.id,
            name: account.name,
            wrapperType: wrapper.wrapperType.toUpperCase(),
            ratio,
            tone: allowanceUsedTone(ratio),
          };
        }),
    [accounts]
  );

  const portfolioPnLFormatted = useMemo(() => {
    const f = formatCurrency(Math.abs(portfolioPnL), defaultCurrency);
    return portfolioPnL > 0 ? `+${f}` : portfolioPnL < 0 ? `-${f}` : f;
  }, [portfolioPnL, defaultCurrency]);

  const formatSignedCurrency = (n: number) => {
    const f = formatCurrency(Math.abs(n), defaultCurrency);
    return n > 0 ? `+${f}` : n < 0 ? `-${f}` : f;
  };

  const portfolioPnLTone =
    portfolioPnL > 0
      ? PNL_TONE_TEXT.gain
      : portfolioPnL < 0
        ? PNL_TONE_TEXT.loss
        : PNL_TONE_TEXT.flat;

  const overviewHero = useOverviewPillarHero({
    netWorth: totals.netWorth,
    accounts,
    isLoading,
  });

  return (
    <PillarTabbedPage
      breadcrumbItems={[{ label: t('nav.overview'), isActive: true }]}
      editMenuExtension={activeTab === 'net-worth' ? netWorthFilterMenu : undefined}
      hero={overviewHero}
      tabs={<OverviewTabs active={activeTab} />}
    >
      {accountsError ? (
        <ErrorState title={t('overview.loadError')} />
      ) : activeTab === 'net-worth' ? (
        <NetWorthTrendCardLazy
          data={netWorthHistory}
          isLoading={isLoading || txLoading}
          selectedAccount={selectedAccount}
        />
      ) : activeTab === 'financial-plan' ? (
        <PlanningOverviewWidget />
      ) : (
        <>
          <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
            <SectionCard
              padding='sm'
              className={taxWrappers.length === 0 ? 'lg:col-span-2' : undefined}
            >
              <h2 className='text-fin-white mb-3 text-sm font-medium'>
                {t('overview.kpis.title')}
              </h2>
              <ul className='flex flex-col gap-0.5'>
                <li className='flex w-full min-w-0 items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-sm'>
                  <span className='text-fin-gray-400 font-medium'>{t('overview.kpis.assets')}</span>
                  <RollingNumber
                    value={isLoading ? totals.totalAssets : 10000}
                    format={(n) => formatCurrency(n, defaultCurrency)}
                    className='text-fin-white shrink-0 font-semibold'
                  />
                </li>
                <li className='flex w-full min-w-0 items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-sm'>
                  <span className='text-fin-gray-400 font-medium'>
                    {t('overview.kpis.liabilities')}
                  </span>
                  <RollingNumber
                    value={isLoading ? totals.totalLiabilities : 10000}
                    format={(n) => formatCurrency(n, defaultCurrency)}
                    className='text-fin-white shrink-0 font-semibold'
                  />
                </li>
                <li className='flex w-full min-w-0 items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-sm'>
                  <span className='text-fin-gray-400 font-medium'>
                    {t('overview.kpis.portfolioPnL')}
                  </span>
                  <RollingNumber
                    value={holdingsLoading ? portfolioPnL : 10000}
                    format={formatSignedCurrency}
                    className={cn('shrink-0 font-semibold', portfolioPnLTone)}
                    aria-label={portfolioPnLFormatted}
                  />
                </li>
              </ul>
            </SectionCard>

            {taxWrappers.length > 0 && (
              <SectionCard padding='sm'>
                <div className='mb-3 flex items-center justify-between gap-2'>
                  <h2 className='text-fin-white text-sm font-medium'>
                    {t('overview.wrappers.title')}
                  </h2>
                  <Link
                    to='/trade/accounts'
                    className='group text-fin-accent hover:text-fin-accent/80 inline-flex items-center gap-0.5 text-xs font-medium'
                  >
                    {t('overview.assetBreakdown.viewTrade')}
                    <ChevronRight
                      className='size-3.5 transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none'
                      aria-hidden
                    />
                  </Link>
                </div>
                <ul className='flex flex-col gap-0.5'>
                  {taxWrappers.map((wrapper) => (
                    <li key={wrapper.id}>
                      <Link
                        to={`/trade/accounts?id=${wrapper.id}`}
                        title={wrapper.name}
                        className='hover:bg-fin-gray-800/40 flex w-full min-w-0 items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors'
                      >
                        <span className='text-fin-white font-medium'>{wrapper.wrapperType}</span>
                        <span
                          className={`shrink-0 font-semibold tabular-nums ${
                            wrapper.tone === 'success'
                              ? 'text-fin-gain'
                              : wrapper.tone === 'warning'
                                ? 'text-fin-warning'
                                : 'text-fin-loss'
                          }`}
                        >
                          <RollingNumber
                            value={wrapper.ratio * 100}
                            format={(n) => `${Math.round(n)}%`}
                          />
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </SectionCard>
            )}
          </div>

          {(assetBreakdown.length > 0 || topPerformers.length > 0) && (
            <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
              {assetBreakdown.length > 0 && (
                <SectionCard padding='sm'>
                  <div className='mb-3 flex items-center justify-between gap-2'>
                    <h2 className='text-fin-white text-sm font-medium'>
                      {t('overview.assetBreakdown.title')}
                    </h2>
                    <Link
                      to='/trade/positions'
                      className='group text-fin-accent hover:text-fin-accent/80 inline-flex items-center gap-0.5 text-xs font-medium'
                    >
                      {t('overview.assetBreakdown.viewTrade')}
                      <ChevronRight
                        className='size-3.5 transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none'
                        aria-hidden
                      />
                    </Link>
                  </div>
                  <StackedAllocationBar segments={allocationSegments} />
                </SectionCard>
              )}

              {topPerformers.length > 0 && (
                <SectionCard padding='sm'>
                  <div className='mb-3 flex items-center justify-between gap-2'>
                    <h2 className='text-fin-white text-sm font-medium'>
                      {t('overview.topPerformers.title')}
                    </h2>
                    <Link
                      to='/trade/positions'
                      className='group text-fin-accent hover:text-fin-accent/80 inline-flex items-center gap-0.5 text-xs font-medium'
                    >
                      {t('overview.topPerformers.viewAll')}
                      <ChevronRight
                        className='size-3.5 transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none'
                        aria-hidden
                      />
                    </Link>
                  </div>
                  <ul className='flex flex-col gap-0.5'>
                    {topPerformers.map((h) => {
                      const isGain = (h.unrealizedPnL ?? 0) >= 0;
                      return (
                        <li key={h.id}>
                          <Link
                            to={`/trade/positions?symbol=${h.assetId}`}
                            className='hover:bg-fin-gray-800/40 flex w-full min-w-0 items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors'
                          >
                            {isGain ? (
                              <TrendingUp
                                className='text-fin-gain h-3.5 w-3.5 shrink-0'
                                aria-hidden
                              />
                            ) : (
                              <TrendingDown
                                className='text-fin-loss h-3.5 w-3.5 shrink-0'
                                aria-hidden
                              />
                            )}
                            <span className='text-fin-white min-w-0 flex-1 truncate font-medium'>
                              {h.assetId}
                            </span>
                            <PnLIndicator
                              amount={h.unrealizedPnL ?? 0}
                              formatted={`${h.pnlPct >= 0 ? '+' : ''}${h.pnlPct.toFixed(1)}%`}
                              className='shrink-0 whitespace-nowrap'
                            />
                            <span className='text-fin-gray-500 shrink-0 text-xs whitespace-nowrap tabular-nums'>
                              {(() => {
                                const f = formatCurrency(
                                  Math.abs(h.unrealizedPnL ?? 0),
                                  defaultCurrency
                                );
                                return (h.unrealizedPnL ?? 0) >= 0 ? `+${f}` : `-${f}`;
                              })()}
                            </span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </SectionCard>
              )}
            </div>
          )}
        </>
      )}
    </PillarTabbedPage>
  );
};

export default Overview;
